import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import ReusableContactForm from '../components/ReusableContactForm/ReusableContactForm';
import styles from './Products.module.css';

// Import product images
import aluminumRailings from '../assets/images/aluminum-railings.webp';
import ironRailings from '../assets/images/iron-railings.webp';
import glassRailings from '../assets/images/glass-railings.webp';
import cableRailings from '../assets/images/cable-railings.webp';
import staircases from '../assets/images/staircases.webp';
import spiralStaircases from '../assets/images/spiral-staircases.webp';
import metalFencing from '../assets/images/metal-fencing.webp';
import customFabrications from '../assets/images/custom-fabrications.webp';
import vinylDecking from '../assets/images/vinyl-decking.webp';

const Products = () => {
  const navigate = useNavigate();
  const [activeIndex, setActiveIndex] = useState(null);

  const handleTouchStart = (index) => {
    setActiveIndex(index);
  };

  const handleTouchEnd = () => {
    setActiveIndex(null);
  };

  const productData = [
    {
      href: "/products/aluminum-railings",
      src: aluminumRailings,
      alt: "Aluminum Railings",
      title: "Aluminum Railings",
      description: "Lightweight, durable, and rust-resistant railings perfect for modern homes and commercial spaces.",
    },
    {
      href: "/products/iron-railings",
      src: ironRailings,
      alt: "Iron Railings",
      title: "Iron Railings",
      description: "Classic and elegant iron railings that combine strength with timeless design.",
    },
    {
      href: "/products/glass-railings",
      src: glassRailings,
      alt: "Glass Railings",
      title: "Glass Railings",
      description: "Sleek and modern glass railings that provide safety without obstructing the view.",
    },
    {
      href: "/products/cable-railings",
      src: cableRailings,
      alt: "Cable Railings",
      title: "Cable Railings",
      description: "Minimalist cable railings offering unobstructed views with modern aesthetics.",
    },
    {
      href: "/products/staircases",
      src: staircases,
      alt: "Staircases",
      title: "Staircases",
      description: "Custom-designed staircases that combine functionality with architectural beauty.",
    },
    {
      href: "/products/spiral-staircases",
      src: spiralStaircases,
      alt: "Spiral Staircases",
      title: "Spiral Staircases",
      description: "Space-saving spiral staircases that make a dramatic design statement.",
    },
    {
      href: "/products/metal-fencing",
      src: metalFencing,
      alt: "Metal Fencing",
      title: "Metal Fencing",
      description: "Secure and stylish metal fencing solutions for both residential and commercial properties.",
    },
    {
      href: "/products/custom-fabrications",
      src: customFabrications,
      alt: "Custom Fabrications",
      title: "Custom Fabrications",
      description: "Tailor-made metalwork solutions to bring your unique design vision to life.",
    },
    {
      href: "/products/vinyl-decking",
      src: vinylDecking,
      alt: "Vinyl Decking",
      title: "Vinyl Decking",
      description: "Low-maintenance, durable vinyl decking options for beautiful outdoor living spaces.",
    },
  ];

  return (
    <div className={styles.container}>
      <Helmet>
        <title>Our Products - ABC Railing</title>
        <meta name="description" content="Explore our premium collection of railing products. From modern to traditional designs, we have the perfect solution for your space." />
      </Helmet>

      <section className={styles.heroSection}>
        <div className={styles.sectionContent}>
          <h2>Our Products</h2>
          <p>Discover our comprehensive range of premium railing and fencing solutions designed to enhance any space with style, safety, and durability.</p>
        </div>
      </section>

      <div className={styles.productsGrid}>
        {productData.map((product, index) => (
          <div
            key={index}
            className={`${styles.productCard} ${activeIndex === index ? styles.active : ''}`}
            onTouchStart={() => handleTouchStart(index)}
            onTouchEnd={handleTouchEnd}
          >
            <a
              href={product.href}
              className={styles.productLink}
              target="_self"
              rel="noopener noreferrer"
              aria-label={product.title}
            >
              <div className={styles.productImageContainer}>
                <img 
                  src={product.src} 
                  alt={product.alt} 
                  className={styles.productImage}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = '/placeholder-product.jpg';
                  }}
                />
                <div className={styles.productOverlay}>
                  <div className={styles.productContent}>
                    <h3 className={styles.productTitle}>{product.title}</h3>
                    <p className={styles.productDescription}>{product.description}</p>
                  </div>
                </div>
              </div>
            </a>
          </div>
        ))}
      </div>

      <section className={styles.consultationSection}>
        <div className={styles.consultationContent}>
          <div className={styles.consultationText}>
            <h2>Need Help Choosing the Right Product?</h2>
            <p>Our experts are here to help you select the perfect railing solution for your project. Contact us today for personalized assistance.</p>
          </div>
          <div className={styles.contactFormSection}>
            <ReusableContactForm 
              title="Get In Touch"
              compact={true}
              className={styles.consultationContactForm}
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default Products;
